import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LoadingState, ErrorState } from "@/components/common";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

export const POLL_MS = 5000;

export function usePoll(load, ms = POLL_MS) {
  useEffect(() => {
    load();
    const id = setInterval(load, ms);
    return () => clearInterval(id);
  }, [load, ms]);
}

function Bubble({ m, tz }) {
  return (
    <div className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${m.mine ? "bg-[#0A2540] text-white" : "border border-slate-200 bg-white text-slate-800"}`}>
        <p className={`text-[11px] font-semibold ${m.mine ? "text-teal-200" : "text-slate-500"}`}>{m.author_name} · {fmtDateTime(m.created_at, tz)}</p>
        {m.text && <p className="mt-1 whitespace-pre-wrap break-words">{m.text}</p>}
        {m.attachments.map((a) => <a key={a.id} href={fileUrl(a.url)} target="_blank" rel="noreferrer"><img src={fileUrl(a.url)} alt="" className="mt-2 max-h-56 rounded-lg" data-testid={`message-photo-${a.id}`} /></a>)}
      </div>
    </div>
  );
}

function Composer({ sendPath, onSent, canAttach }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) return;
    const fd = new FormData();
    fd.append("text", text);
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      await api.post(sendPath, fd);
      setText("");
      setFile(null);
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={send} className="border-t border-slate-200 p-3" data-testid="message-composer">
      {file && <p className="mb-2 flex items-center gap-2 text-xs text-slate-600">{file.name}<button type="button" onClick={() => setFile(null)} data-testid="message-remove-photo"><X className="h-3 w-3" /></button></p>}
      <div className="flex items-end gap-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("msg.placeholder")} className="min-h-[44px] flex-1 resize-none" data-testid="message-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} />
        {canAttach && <>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="message-photo-input" />
          <Button type="button" variant="outline" size="icon" onClick={() => ref.current?.click()} aria-label={t("msg.attach")} data-testid="message-attach-button"><ImagePlus className="h-4 w-4" /></Button>
        </>}
        <Button type="submit" size="icon" disabled={busy} aria-label={t("msg.send")} data-testid="message-send-button"><Send className="h-4 w-4" /></Button>
      </div>
    </form>
  );
}

export function Thread({ path, sendPath, tz, readOnly, canAttach = true, onChange, title, subtitle, emptyText }) {
  const [state, setState] = useState({ loading: true });
  const end = useRef();
  const count = useRef(0);
  const load = useCallback(() => api.get(path).then((r) => { setState({ data: r.data }); onChange?.(); }).catch((e) => setState((s) => (s.data ? s : { error: errMsg(e) }))), [path]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(load);
  const n = state.data?.messages.length || 0;
  useEffect(() => { if (n !== count.current) { count.current = n; end.current?.scrollIntoView({ block: "nearest" }); } }, [n]);
  if (state.loading) return <LoadingState />;
  if (state.error) return <ErrorState message={state.error} onRetry={load} />;
  const c = state.data;
  return (
    <div className="flex h-full min-h-[420px] flex-col" data-testid="message-thread">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="font-semibold text-slate-900" data-testid="thread-subject">{title || c.subject || t("msg.general")}</p>
        <p className="text-xs text-slate-500">{subtitle ?? c.customer?.name}</p>
      </div>
      <div className="max-h-[55vh] flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4" data-testid="thread-messages">
        {c.messages.length === 0 && emptyText && <p className="py-8 text-center text-sm text-slate-500" data-testid="thread-empty">{emptyText}</p>}
        {c.messages.map((m) => <Bubble key={m.id} m={m} tz={tz} />)}
        <div ref={end} />
      </div>
      {readOnly ? <p className="border-t p-3 text-xs text-slate-500">{t("msg.readOnly")}</p> : <Composer sendPath={sendPath} onSent={load} canAttach={canAttach} />}
    </div>
  );
}

export function ConvRow({ c, active, onClick, tz, staff }) {
  return (
    <button type="button" onClick={onClick} data-testid={`conversation-row-${c.id}`}
      className={`w-full border-b border-slate-100 px-4 py-3 text-left transition-colors duration-150 hover:bg-slate-50 ${active ? "bg-teal-50" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-slate-900">{staff ? c.customer.name : c.subject || t("msg.general")}</span>
        {c.unread > 0 && <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid={`conversation-unread-${c.id}`}>{c.unread}</span>}
      </div>
      {staff && <p className="truncate text-xs font-medium text-slate-700" data-testid={`conversation-vehicle-${c.id}`}>{c.subject}</p>}
      <p className="truncate text-xs text-slate-500">{c.last_author === (staff ? "staff" : "customer") && `${t("msg.you")}: `}{c.last_preview}</p>
      {c.last_message_at && <p className="text-[11px] text-slate-400">{fmtDateTime(c.last_message_at, tz)}</p>}
    </button>
  );
}
